import * as React from "react"
import Header from "@app/components/Header"
import { Outlet, redirect, useLocation, useNavigation, useParams, useRouteLoaderData } from "react-router-dom"
import { clearInspections, getInspections } from "@app/models/inspection"
import { getSetting, getUserProfile, setUserProfile } from "@app/models/settings"
import { clearOffices, getOffices } from "@app/models/office"
import Config from "@app/constants/Config"
import BlockingLoader from "@app/components/BlockingLoader"
import { clearInspectionConfig, getInspectionConfig } from "@app/models/inspectionConfig"
import { useFormStepConfig } from "@app/components/inspection-form/FormStepContainer.jsx"
import SaveInspectionContextContainer
	from "@app/components/SaveInspectionContextContainer/SaveInspectionContextContainer.jsx"
import useServiceWorkerUpdate from "@app/lib/hooks/userServiceWorkerUpdate"
import { getFormStepErrors } from "@app/utils/inspection"
import { Badge } from "react-bootstrap"
import { clearJobs, getJobs } from "@app/models/job"
import { useEffect } from "react"
import SignsOverlayContextProvider from "@app/components/SignsOverlayContext/SignsOverlayContext"
import { useAuthRedirect } from "@app/utils/helper"

export const loader = async ({ request }) => {
	const [offices, officeId, inspections, , jobsData, userProfile] = await Promise.all([
		getOffices(),
		getSetting("officeId"),
		getInspections(),
		getInspectionConfig(),
		getJobs(),
		getUserProfile()
	])

	const { jobs, measuresByInfluence, measuresCatalogue } = jobsData

	const office = offices.find(({ ID }) => ID.toString() === officeId)
	if (!office && new URL(request.url).pathname !== `${Config.BASE_PATH}/office`) {
		return redirect(`${Config.BASE_PATH}/office`)
	}

	const companyIds = office?.kundenliste?.map(({ KUNDENNR }) => KUNDENNR.toString()) || []

	// TODO inspections filtern, aktuelles office liefern
	return {
		inspections: inspections.filter(({ companyId }) => companyIds.includes(companyId?.toString())),
		office,
		offices,
		jobs,
		measuresByInfluence,
		measuresCatalogue,
		userProfile
	}
}

export const useNavigationConfig = ({ signsOverlayHandler }) => {
	const params = useParams()
	const currentStep = parseInt(params.step, 10) || 1
	const { inspectionConfig, inspection } = useRouteLoaderData("inspection") || {}
	const { pathname } = useLocation()

	let meta = []
	let items = [
		{
			label: "Übersicht",
			to: `/`,
			isOverview: true,
			noCounter: true
		}
	]
	let title = "Arbeitsplatzbesichtigung"
	const jobIsSet = inspection?.values?.taetigkeit_beschreibung_ueberlassungals?.length > 0

	if (inspectionConfig && inspection?.uuid && !pathname.match(/\/inspection\/.+\/archive/)) {
		items = [
			...items,
			...inspectionConfig.forms.steps
				.map(({ header }, step) => {
					const errors = getFormStepErrors(inspectionConfig?.forms?.steps?.[step], inspection.values)?.length
					return ({
						label: <>{header} {errors > 0 &&
							<Badge bg="secondary" style={{ alignSelf: "flex-start" }}>{errors}</Badge>}</>,
						to: `/inspection/${inspection.uuid}/${step + 1}`,
						enabled: jobIsSet ? true : step <= 1,
					})
				}),
			{
				label: "Zusammenfassung",
				to: `/inspection/${inspection.uuid}/summary`,
				enabled: jobIsSet
			},
			{
				label: "Festsetzung",
				to: `/inspection/${inspection.uuid}/confirmation`,
				enabled: jobIsSet
			}
		]

		if (signsOverlayHandler?.enabled && !pathname.match(/\/inspection\/.+\/summary/) && !pathname.match(/\/inspection\/.+\/confirmation/)) {
			meta.push({
				label: "Warnhinweisschilder erfassen",
				noCounter: true,
				onClick: signsOverlayHandler.onClick,
				preventDefault: true,
				type: "primary-button",
			})
		}
	} else {
		// title = "Menu";
	}

	//if(Config.ENV !== "prod") {
	meta.push({
			label: "DB Cache leeren",
			noCounter: true,
			onClick: async () => {
				console.log("DB cahce leeren")
				await Promise.all([clearOffices(), clearInspections(), clearInspectionConfig(), clearJobs()])
				await Promise.all([getOffices(), getInspections(), getInspectionConfig(), getJobs()])
			}
		}
	)
	//}

	return { title, items, meta }
}
const useInspectionSave = () => {
	const { inspection } = useRouteLoaderData("inspection") || {}
	if (inspection) {
		return {
			saveHandler: () => {
			}
		}
	} else {
		return {
			saveHandler: null
		}
	}
}

const useTitles = (location, formStepConfig) => {
	const { pathname } = location
	const { inspection } = useRouteLoaderData("inspection") || {}
	const job = inspection?.values?.taetigkeit_beschreibung_ueberlassungals
	const jobAppendix = job ? ` für ${job}` : ""
	const topTitle = formStepConfig?.followUp
		? `Folgebesichtigung ${jobAppendix}`
		: `Neue Begehung ${jobAppendix}`

	if (pathname.match(/^\/inspection\/.+\/summary$/)) {
		return {
			title: "Zusammenfassung",
			topTitle
		}
	} else if (pathname.match(/^\/inspection\/.+\/confirmation$/)) {
		return {
			title: "Festsetzung",
			topTitle
		}
	} else if (pathname.match(/^\/inspection\/.+\/thankyou$/)) {
		return {
			// title: "Erfolg",
			// topTitle
		}
	} else if (formStepConfig) {
		return {
			title: formStepConfig?.header,
			topTitle
		}
	} else {
		return {
			title: null,
			topTitle: null
		}
	}
}

function ScrollToTop () {
	const { pathname } = useLocation()

	useEffect(() => {
		window.scrollTo(0, 0)
	}, [pathname])

	return null
}

const isBlockingLoadState = (state, location) => {
	const { pathname } = location

	// saveInspection calls run in the background, so suppress this short flickering of the blocking loader
	return state === "loading" &&
		(!pathname.startsWith("/inspection") || pathname.match(/^\/inspection\/.+\/confirmation$/))

}

export default function Root () {
	const { pathname } = useLocation()
	const { saveHandler } = useInspectionSave()
	const { state } = useNavigation()
	let location = useLocation()
	const formStepConfig = useFormStepConfig()
	const { title, topTitle } = useTitles(location, formStepConfig)
	const { updateAvailable, reloadPage } = useServiceWorkerUpdate()
	const { pendingRedirect } = useAuthRedirect(!updateAvailable)

	React.useEffect(() => {
		if (updateAvailable && !location.pathname.startsWith("/inspection/") && !pendingRedirect) {
			console.log("Es ist eine neue Version der App verfügbar. Sie wird jetzt geladen.")
			alert("Es ist eine neue Version der App verfügbar. Sie wird jetzt geladen.")
			reloadPage()
		}
	}, [updateAvailable, pendingRedirect])

	return (
		<div className={"arbeitsplatzbesichtigung"}>
			<ScrollToTop />
			<SignsOverlayContextProvider>
				<SaveInspectionContextContainer>
					<Header
						showNavi={
						!["/", "/office", "/inspection/archive"].includes(pathname) &&
						!pathname.match(/^\/inspection\/.+\/thankyou$/)
					}
						title={title}
						topTitle={topTitle}
						saveButtonEnable={!!saveHandler}
						onClickSave={saveHandler}
					/>
					<main>
						{updateAvailable && (
							<div>
								<p>Eine neue Version ist verfügbar!</p>
								<button onClick={reloadPage}>Jetzt aktualisieren</button>
							</div>
						)}
						<BlockingLoader loading={isBlockingLoadState(state, location)} />
						<Outlet />
					</main>
				</SaveInspectionContextContainer>
			</SignsOverlayContextProvider>
		</div>
	)
}
